import { HicapsConnectMerchant } from '@principle-theorem/principle-core';
import {
  IBridgeDevice,
  IHicapsConnectMerchant,
  IHicapsConnectTerminalV2,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef } from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import {
  HicapsConnectV2Routing,
  IHicapsConnectRouteResult,
} from './hicaps-connect-v2-routing';

export interface IHicapsConnectV2TerminalMerchant {
  terminal: WithRef<IHicapsConnectTerminalV2>;
  merchantId: string;
  merchant: WithRef<IHicapsConnectMerchant> | undefined;
}

export interface IHicapsV2PairEntry {
  entryId: string;
  terminal: WithRef<IHicapsConnectTerminalV2>;
  merchantId: string;
  merchant: WithRef<IHicapsConnectMerchant> | undefined;
  merchantDisplayName: string;
  route: IHicapsConnectRouteResult | undefined;
}

export interface IHicapsV2TerminalGroup {
  terminalId: string;
  terminalName: string;
  entries: IHicapsV2PairEntry[];
}

/**
 * Owns the terminal/merchant pair as a selectable thing. A payment is sent to a
 * pair, not to a terminal, so both the automatic selection in
 * `HicapsConnectV2TerminalSelectorService` and the dialog a staff member picks
 * from are built from the same pairing and grouping rules.
 */
export class HicapsConnectV2TerminalMerchants {
  /**
   * Expands terminals into the pairs a payment can actually be sent to, leaving
   * out merchants the user has disabled on a terminal.
   */
  static fromTerminals(
    terminals: WithRef<IHicapsConnectTerminalV2>[],
    merchants: WithRef<IHicapsConnectMerchant>[]
  ): IHicapsConnectV2TerminalMerchant[] {
    return terminals.flatMap((terminal) =>
      Object.entries(terminal.merchants)
        .filter(([, edge]) => HicapsConnectMerchant.isEdgeActive(edge))
        .map(([merchantId]) => ({
          terminal,
          merchantId,
          merchant: merchants.find((item) => item.merchantId === merchantId),
        }))
    );
  }

  /**
   * A pair has no document of its own, so selection state is tracked against a
   * composite of the two ids that define it.
   */
  static entryId(terminalMerchant: IHicapsConnectV2TerminalMerchant): string {
    return `${terminalMerchant.terminal.terminalId}-${terminalMerchant.merchantId}`;
  }

  /**
   * Nests merchants under the terminal that carries them, because a staff
   * member picks the physical terminal first and the provider second. Both
   * levels are sorted by the name shown so the list does not reorder between
   * syncs.
   */
  static toGroups(
    terminalMerchants: IHicapsConnectV2TerminalMerchant[],
    bridgeDevices: WithRef<IBridgeDevice>[]
  ): IHicapsV2TerminalGroup[] {
    const groups = new Map<string, IHicapsV2TerminalGroup>();

    for (const terminalMerchant of terminalMerchants) {
      const { terminal, merchantId, merchant } = terminalMerchant;
      const entry: IHicapsV2PairEntry = {
        entryId: HicapsConnectV2TerminalMerchants.entryId(terminalMerchant),
        terminal,
        merchantId,
        merchant,
        merchantDisplayName: HicapsConnectMerchant.displayName(
          merchant,
          merchantId
        ),
        route: HicapsConnectV2Routing.resolveRoute(terminal, bridgeDevices),
      };

      const existing = groups.get(terminal.terminalId);
      if (existing) {
        existing.entries.push(entry);
        continue;
      }

      groups.set(terminal.terminalId, {
        terminalId: terminal.terminalId,
        terminalName: terminal.name,
        entries: [entry],
      });
    }

    return sortBy([...groups.values()], (group) => group.terminalName).map(
      (group) => ({
        ...group,
        entries: sortBy(group.entries, (entry) => entry.merchantDisplayName),
      })
    );
  }
}
